import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { Seo } from "@/components/site/Seo";
import { PageHero } from "@/components/site/PageHero";
import { postForm, errorMessage } from "@/lib/api";

const EDUCATION = ["No Formal Education", "High School Diploma", "Equivalent (GED)", "Some College (No Degree)", "Associate Degree", "Bachelor's Degree", "Graduate School"];
const HOURS = ["Less than 10 hours per week", "11–20 hours per week", "21–30 hours per week", "More than 31 hours per week"];
const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const TRANSPORT = ["I have a car", "I don't have a car, but I have my license", "I don't have my license"];
const STORES = [
  "TeaDM Plus (Falls Church) 6779 Wilson Blvd, Falls Church, VA 22044",
  "TeaDM Mini (Vienna) 142 Maple Ave W, Vienna, VA 22180",
  "TeaDM (Fairfax) 9416 Fairfax Blvd, Fairfax, VA 22031",
  "TeaDM Street Food Corner (Sterling) 46515 Leesburg Pike, Sterling, VA 20164",
];
const EMPTY = { full_name: "", email: "", phone: "", age: "", prior_experience: "", experience_details: "", education_level: "", languages: "", hours_per_week: "", available_days: [], availability_times: "", transportation: "", locations: [], open_to_multiple: "", worker_type: "" };

const Radios = ({ name, options, value, onChange, testId }) => (
  <div className="flex flex-wrap gap-2" role="radiogroup">
    {options.map((o) => (
      <label key={o} className={`cursor-pointer rounded-full border-2 px-4 py-2.5 text-sm font-semibold transition-colors duration-300 ${value === o ? "border-[#ff2e63] bg-[#ff2e63] text-white" : "border-[#1a0a10]/15 bg-white hover:border-[#1a0a10]/40"}`}>
        <input type="radio" name={name} value={o} checked={value === o} onChange={() => onChange(o)} className="sr-only" data-testid={`${testId}-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} />
        {o}
      </label>
    ))}
  </div>
);

const Checks = ({ options, values, onToggle, testId, block }) => (
  <div className={block ? "grid gap-2" : "flex flex-wrap gap-2"}>
    {options.map((o, i) => (
      <label key={o} className={`cursor-pointer rounded-2xl border-2 px-4 py-2.5 text-sm font-semibold transition-colors duration-300 ${values.includes(o) ? "border-[#2e9e4f] bg-[#2e9e4f] text-white" : "border-[#1a0a10]/15 bg-white hover:border-[#1a0a10]/40"}`}>
        <input type="checkbox" checked={values.includes(o)} onChange={() => onToggle(o)} className="sr-only" data-testid={`${testId}-${i}`} />
        {o}
      </label>
    ))}
  </div>
);

const Field = ({ label, children, full }) => (
  <fieldset className={full ? "sm:col-span-2" : ""}>
    <legend className="label">{label}</legend>
    {children}
  </fieldset>
);

export default function Careers() {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));
  const toggle = (k) => (o) => setF((p) => ({ ...p, [k]: p[k].includes(o) ? p[k].filter((x) => x !== o) : [...p[k], o] }));

  const submit = async (e) => {
    e.preventDefault();
    const missing = [["prior_experience", "prior job experience"], ["education_level", "education level"], ["hours_per_week", "hours per week"], ["transportation", "car / license"], ["open_to_multiple", "multiple locations"], ["worker_type", "seasonal or permanent"]].find(([k]) => !f[k]);
    if (missing) return toast.error(`Please select an option for ${missing[1]}.`);
    if (!f.available_days.length) return toast.error("Please select at least one available day.");
    if (!f.locations.length) return toast.error("Please select at least one location.");
    if (f.prior_experience === "Yes" && !f.experience_details.trim()) return toast.error("Please tell us about your prior experience.");
    setBusy(true);
    try {
      await postForm("/careers", f);
      setDone(true);
      toast.success("Application received — thank you!");
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
    return undefined;
  };

  return (
    <>
      <Seo title="Careers at TeaDM — Bubble Tea Jobs in Northern Virginia" description="Join the TeaDM team in Falls Church, Vienna, Fairfax or Sterling, VA. Apply online for seasonal or permanent positions at our bubble tea and coffee shops." path="/careers" />
      <PageHero testId="careers-hero" bg="#2e9e4f" eyebrow="Careers" lines={["Join our", "team."]} intro="Love boba, coffee and good music? Apply to work at TeaDM — choose one or more of our stores below." />
      <section className="bg-[#fff5e8] py-20 sm:py-28">
        <div className="wrap max-w-4xl">
          {done ? (
            <div data-testid="careers-success" className="rounded-[2rem] bg-[#ff2e63] p-10 text-[#fff5e8]">
              <p className="font-display text-4xl font-extrabold">Thanks for applying!</p>
              <p className="mt-3">We've received your application and will get back to you as soon as we can.</p>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="careers-form" className="grid gap-7 rounded-[2rem] bg-[#efe4d6] p-6 sm:grid-cols-2 sm:p-10">
              <Field label="Full Name *"><input required value={f.full_name} onChange={set("full_name")} className="field" data-testid="careers-name-input" /></Field>
              <Field label="Email *"><input type="email" required value={f.email} onChange={set("email")} className="field" data-testid="careers-email-input" /></Field>
              <Field label="Phone Number *"><input type="tel" required minLength={7} value={f.phone} onChange={set("phone")} className="field" data-testid="careers-phone-input" /></Field>
              <Field label="Age (if under 18, please put your birthday as well) *"><input required value={f.age} onChange={set("age")} className="field" data-testid="careers-age-input" /></Field>
              <Field label="Prior Job Experience? *" full><Radios name="exp" options={["Yes", "No"]} value={f.prior_experience} onChange={set("prior_experience")} testId="careers-experience" /></Field>
              {f.prior_experience === "Yes" && (
                <Field label="If yes, where did you work? What was your position and what experience was gained there? *" full>
                  <textarea rows={4} value={f.experience_details} onChange={set("experience_details")} className="field resize-none" data-testid="careers-experience-details" />
                </Field>
              )}
              <Field label="Education Level *">
                <select value={f.education_level} onChange={set("education_level")} className="field" data-testid="careers-education-select">
                  <option value="">Select…</option>
                  {EDUCATION.map((o) => <option key={o}>{o}</option>)}
                </select>
              </Field>
              <Field label="Languages (Spoken/Written/Listening) *"><input required value={f.languages} onChange={set("languages")} className="field" data-testid="careers-languages-input" /></Field>
              <Field label="How many hours can you work a week? *" full><Radios name="hours" options={HOURS} value={f.hours_per_week} onChange={set("hours_per_week")} testId="careers-hours" /></Field>
              <Field label="When are you available to work? (Select all that apply) *" full><Checks options={DAYS} values={f.available_days} onToggle={toggle("available_days")} testId="careers-day" /></Field>
              <Field label="Enter the time you're available each day, e.g., Mon: 9 AM–5 PM" full><textarea rows={3} value={f.availability_times} onChange={set("availability_times")} className="field resize-none" data-testid="careers-times-input" /></Field>
              <Field label="Do you have a car / do you have a license? *" full><Radios name="car" options={TRANSPORT} value={f.transportation} onChange={set("transportation")} testId="careers-transport" /></Field>
              <Field label="Which location would you like to work? Select 1 or more *" full><Checks block options={STORES} values={f.locations} onToggle={toggle("locations")} testId="careers-location" /></Field>
              <Field label="Open to working at multiple locations? *"><Radios name="multi" options={["Yes", "No"]} value={f.open_to_multiple} onChange={set("open_to_multiple")} testId="careers-multiple" /></Field>
              <Field label="Select One *"><Radios name="worker" options={["Seasonal Worker", "Permanent Worker"]} value={f.worker_type} onChange={set("worker_type")} testId="careers-worker" /></Field>
              <button type="submit" disabled={busy} className="btn btn-berry justify-self-start disabled:opacity-60 sm:col-span-2" data-testid="careers-submit-btn">{busy ? "Sending…" : "Submit application"} <ArrowRight className="h-4 w-4" /></button>
            </form>
          )}
        </div>
      </section>
    </>
  );
}
